import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Copy, 
  CheckCircle2, 
  Church, 
  Stethoscope, 
  GraduationCap, 
  Users2, 
  Flame,
  Banknote
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const Support = () => {
  const { toast } = useToast();

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Account Copied",
      description: `${label} account number is now on your clipboard.`,
    });
  };

  const initiatives = [
    { icon: <Flame className="w-5 h-5" />, text: "Evangelism & Mission Outreach" },
    { icon: <Users2 className="w-5 h-5" />, text: "Clergy & Staff Welfare" },
    { icon: <Church className="w-5 h-5" />, text: "Church Infrastructure Maintenance" },
    { icon: <Stethoscope className="w-5 h-5" />, text: "Health & Mobile Clinics" },
    { icon: <GraduationCap className="w-5 h-5" />, text: "Scholarships & Education Grants" },
    { icon: <Heart className="w-5 h-5" />, text: "Youth Empowerment Programs" },
  ];

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-20 px-4">
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="inline-flex items-center justify-center p-3 bg-amber-100 rounded-2xl text-amber-700 mb-6"
          >
            <Heart className="w-8 h-8 fill-current" />
          </motion.div>
          <h1 className="text-4xl md:text-6xl font-serif font-bold text-slate-900 mb-6">
            Giving & Stewardship
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto font-serif leading-relaxed">
            Your generous support fuels our mission to spread the Gospel and serve the community. 
            Every gift, no matter the size, makes an eternal impact.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left: Why Support Matters */}
          <div className="lg:col-span-7 space-y-8">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-sm border border-slate-100">
              <h2 className="text-2xl font-serif font-bold text-slate-900 mb-6 flex items-center gap-3">
                Mission Impact
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed font-serif text-lg">
                As a Diocese, we are committed to advancing God’s kingdom through worship, education, 
                and social action. Your partnership enables us to:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {initiatives.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-100/50">
                    <div className="text-amber-600 mt-1">{item.icon}</div>
                    <span className="text-sm font-medium text-slate-700 leading-tight">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-8 bg-slate-900 rounded-[2.5rem] text-center relative overflow-hidden">
               <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
               <p className="text-xl md:text-2xl font-serif italic text-amber-100 relative z-10">
                 “Each of you should give what you have decided in your heart to give... for God loves a cheerful giver.”
               </p>
               <p className="mt-4 text-amber-500 font-bold uppercase tracking-widest text-xs relative z-10">— 2 Corinthians 9:7</p>
            </div>
          </div>

          {/* Right: Bank Details */}
          <div className="lg:col-span-5 space-y-6">
            <h2 className="text-xl font-serif font-bold text-slate-900 px-2 flex items-center gap-2">
              <Banknote className="w-5 h-5 text-amber-600" />
              Direct Transfer
            </h2>

            <AccountCard 
              label="Current Account" 
              number="2001017784" 
              onCopy={() => copyToClipboard("2001017784", "Current")}
            />

            <AccountCard 
              label="Savings Account" 
              number="3017518067" 
              onCopy={() => copyToClipboard("3017518067", "Savings")}
            />

            <div className="p-6 rounded-3xl bg-amber-50 border border-amber-100">
              <div className="flex gap-3">
                <CheckCircle2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 leading-relaxed">
                  <strong>First Bank Nigeria:</strong> Please include a description (e.g., "Tithe", "Seed", "Donation") 
                  to help us direct your gift to the right fund.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// Sub-component for Account Cards
const AccountCard = ({ label, number, onCopy }: { label: string, number: string, onCopy: () => void }) => (
  <div className="relative group">
    <div className="absolute inset-0 bg-white rounded-[2rem] shadow-md translate-x-1.5 translate-y-1.5 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform border border-slate-100" />
    <Card className="relative z-10 rounded-[2rem] border-none shadow-xl bg-white overflow-hidden">
      <CardContent className="p-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mb-1">Account Type</p>
            <h3 className="text-lg font-serif font-bold text-slate-900">{label}</h3>
          </div>
          <div className="p-2 bg-slate-50 rounded-lg">
            <Banknote className="w-5 h-5 text-slate-400" />
          </div>
        </div>
        
        <div className="mt-6">
           <p className="text-xs font-medium text-slate-500 mb-1">Account Name</p>
           <p className="text-sm font-bold text-slate-800 uppercase tracking-wide">Ibadan South Diocese</p>
        </div>

        <div className="mt-6 flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100 group-hover:border-amber-200 transition-colors">
          <div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">Account Number</p>
            <p className="text-xl font-mono font-bold text-slate-900">{number}</p>
          </div>
          <button 
            onClick={onCopy}
            className="p-3 bg-white shadow-sm border border-slate-200 rounded-xl text-slate-600 hover:text-amber-600 hover:border-amber-200 transition-all"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default Support;